import React, { useState, useEffect, useRef, useCallback } from "react";
import { useAtom } from "jotai";

import useCRUDService from "../../../../hooks/useCRUDService";
import { useTranslation } from "react-i18next";

import { Toast } from "primereact/toast";
import { Tag } from "primereact/tag";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { SelectButton } from "primereact/selectbutton";

import Layout from "../../../../layout/layout";
import Header from "../../../../layout/header";
import ClientsForm from "./clientsForm";

import { loadingState } from "../../../../states/loadingState";

const sectionStyle = {
  background: "var(--color-surface)",
  border: "1px solid var(--color-border)",
  borderRadius: 12,
  overflow: "hidden",
};

const Field = ({ label, children }) => (
  <div
    className="flex align-items-center gap-3 py-2"
    style={{ borderBottom: "1px solid var(--color-border)" }}
  >
    <span
      style={{
        minWidth: 200,
        flexShrink: 0,
        color: "var(--color-text-light)",
        fontSize: 13,
        fontWeight: 600,
      }}
    >
      {label}
    </span>
    <span style={{ color: "var(--color-text)", fontSize: 13 }}>{children}</span>
  </div>
);

const Clients = () => {
  const [isLoading, setIsLoading] = useAtom(loadingState);
  const { t } = useTranslation();
  const CRUDService = useCRUDService("clients");
  const toast = useRef(null);

  const [formDialog, setFormDialog] = useState(false);
  const [client, setClient] = useState({});
  const [installmentsAmounts, setInstallmentsAmounts] = useState([]);
  const [installmentsNumbers, setInstallmentsNumbers] = useState([]);

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    try {
      const data = await CRUDService.getEntity(
        localStorage.getItem("clientId"),
        "base"
      );
      setClient(data);
      setInstallmentsAmounts(
        (data.clientsInstallmentsAmountsCollection ?? []).map(
          (a) => a.installmentAmount
        )
      );
      setInstallmentsNumbers(
        (data.clientsInstallmentsNumbersCollection ?? []).map(
          (i) => i.installmentNumber
        )
      );
    } catch (error) {
      console.error(t("forms.messages.operation.error"), error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [formDialog]);

  const hideDialog = (action) => {
    setFormDialog(false);
    if (action !== "CANCEL") fetchData();
  };

  const [formSettings, setFormSettings] = useState({
    entityId: -1,
    formMode: "",
    hideDialog: hideDialog,
    toast: toast,
    CRUDService: CRUDService,
  });

  const openFormDialog = () => {
    setFormSettings((prev) => ({
      ...prev,
      formMode: "UPDATE",
      entityId: client?.id,
      hideDialog: hideDialog,
      toast: toast,
      CRUDService: CRUDService,
    }));
    setFormDialog(true);
  };

  const getDialogHeader = () => (
    <div className="abm-form-hero">
      <h2 className="abm-form-hero-title">{`${t("Edit")} ${t("entities.clients.singular")}`}</h2>
      <p className="abm-form-hero-subtitle">
        Complete la información para gestionar el registro.
      </p>
    </div>
  );

  return (
    <Layout
      header={
        <Header
          icon={<i className="pi pi-building" />}
          title={t("entities.clients.singular")}
          actions={
            <Button
              icon="pi pi-pencil"
              label="Editar"
              className="p-button-primary"
              onClick={openFormDialog}
            />
          }
        />
      }
    >
      <Toast ref={toast} />

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: 20,
          marginBottom: 20,
        }}
      >
        {/* Información general */}
        <div style={sectionStyle}>
          <div className="wh-section-title">Información General</div>
          <div style={{ padding: "4px 20px 0" }}>
            <Field label={t("entities.clients.name")}>{client?.name}</Field>
            <Field label={t("entities.clients.rut")}>{client?.rut}</Field>
            <Field label={t("entities.clients.address")}>{client?.address}</Field>
            <Field label={t("entities.clients.email")}>{client?.email}</Field>
            <Field label={t("entities.clients.phone")}>{client?.phone}</Field>
            <Field label={t("entities.clients.notes")}>{client?.notes || "—"}</Field>
            <Field label={t("entities.clients.active")}>
              <Tag
                value={client?.active ? "Activo" : "Inactivo"}
                severity={client?.active ? "success" : "danger"}
              />
            </Field>
          </div>
        </div>

        {/* Configuración */}
        <div style={sectionStyle}>
          <div className="wh-section-title">Configuración</div>
          <div style={{ padding: "4px 20px 0" }}>
            <Field
              label={t(
                "entities.clients.clientsSettings.administrationFeeRate"
              )}
            >
              {client?.clientsSettings?.administrationFeeRate ?? "—"}
            </Field>
            <Field
              label={t("entities.clients.clientsSettings.annualDefaultRate")}
            >
              {client?.clientsSettings?.annualDefaultRate ?? "—"}
            </Field>
            <Field label={t("entities.clients.clientsSettings.annualRate")}>
              {client?.clientsSettings?.annualRate ?? "—"}
            </Field>
          </div>
        </div>
      </div>

      {/* Montos y plazos habilitados */}
      <div
        style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20 }}
      >
        <div style={sectionStyle}>
          <div className="wh-section-title">
            {t("entities.clients.installmentsAmounts")}
          </div>
          <div className="flex justify-content-center flex-wrap p-4">
            <SelectButton
              options={installmentsAmounts}
              className="amount"
              disabled
            />
          </div>
        </div>
        <div style={sectionStyle}>
          <div className="wh-section-title">
            {t("entities.clients.installmentesNumbers")}
          </div>
          <div className="flex justify-content-center flex-wrap p-4">
            <SelectButton
              options={installmentsNumbers}
              className="installments"
              disabled
            />
          </div>
        </div>
      </div>

      <Dialog
        visible={formDialog}
        header={getDialogHeader()}
        modal
        className="flex w-9"
        onHide={hideDialog}
      >
        <ClientsForm settings={formSettings} />
      </Dialog>
    </Layout>
  );
};

export default Clients;
